import React from 'react';
import Link from 'next/link';
import clsx from 'clsx';

import { partyToneClass } from '../../../../src/components/GovTrades/ColumnsDef/memberBadges';

export type SidebarRowTone = 'pos' | 'neg' | 'neutral';

export interface SidebarRowProps {
  href?: string;
  linkTitleOnly?: boolean;
  rank?: number;
  media?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  value?: React.ReactNode;
  valueTone?: SidebarRowTone;
  valueChip?: boolean;
  subValue?: React.ReactNode;
  pill?: { label: React.ReactNode; tone: 'buy' | 'sell' };
  party?: string;
  tinted?: boolean;
  className?: string;
  uppercaseTitle?: boolean;
  uppercaseSubtitle?: boolean;
}

const toneText = (tone?: SidebarRowTone) =>
  tone === 'pos' ? 'text-[#0f993d]' : tone === 'neg' ? 'text-[#ff4050]' : 'text-bzgray-900';

export const SidebarRow = ({
  href,
  linkTitleOnly = false,
  rank,
  media,
  title,
  subtitle,
  value,
  valueTone,
  valueChip = false,
  subValue,
  pill,
  party,
  tinted = false,
  className,
  uppercaseTitle = false,
  uppercaseSubtitle = false,
}: SidebarRowProps) => {
  const wholeRowLink = href && !linkTitleOnly;
  const rowClassName = clsx(
    'flex items-center gap-2 rounded-lg px-2 py-1.5 no-underline',
    tinted && 'bg-[#f2f8ff]',
    party && partyToneClass(party),
    wholeRowLink && 'transition-colors duration-150 hover:bg-[#e9f2ff]',
    className,
  );

  const titleNode =
    href && linkTitleOnly ? (
      <Link
        className={clsx('truncate text-sm font-bold leading-tight text-bzgray-900 hover:text-bzblue-600', uppercaseTitle && 'uppercase')}
        href={href}
      >
        {title}
      </Link>
    ) : (
      <span className={clsx('truncate text-sm font-bold leading-tight text-bzgray-900', uppercaseTitle && 'uppercase')}>{title}</span>
    );

  const inner = (
    <>
      {typeof rank === 'number' ? (
        <span className="w-4 shrink-0 text-center text-[13px] font-bold text-[#8b97a9]">{rank}</span>
      ) : null}
      {media ? (
        <span className="flex shrink-0 items-center [&_img]:h-8 [&_img]:w-8 [&_img]:rounded-md [&_img]:bg-white [&_img]:object-cover [&_svg]:h-8 [&_svg]:w-8 [&_svg]:rounded-md [&_svg]:bg-white">
          {media}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {titleNode}
        {subtitle ? (
          <span className={clsx('truncate text-xs leading-[1.3] text-bzgray-600', uppercaseSubtitle && 'uppercase')}>{subtitle}</span>
        ) : null}
      </span>
      {value != null || subValue != null || pill ? (
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {value != null || subValue != null ? (
            <span className="flex flex-col items-end">
              {value != null ? (
                <span
                  className={clsx(
                    'whitespace-nowrap text-sm font-bold',
                    toneText(valueTone),
                    valueChip && 'rounded-md px-2 py-0.5',
                    valueChip && valueTone === 'pos' && 'bg-[#e7f7ee]',
                    valueChip && valueTone === 'neg' && 'bg-[#ffecee]',
                  )}
                >
                  {value}
                </span>
              ) : null}
              {subValue != null ? <span className="whitespace-nowrap text-xs leading-[1.3] text-bzgray-600">{subValue}</span> : null}
            </span>
          ) : null}
          {pill ? (
            <span
              className={clsx(
                'rounded px-2 py-1 text-[13px] font-bold uppercase',
                pill.tone === 'buy' ? 'bg-[#dff3f0] text-[#0f993d]' : 'bg-[#f3e6ee] text-[#ff4050]',
              )}
            >
              {pill.label}
            </span>
          ) : null}
        </span>
      ) : null}
    </>
  );

  return wholeRowLink ? (
    <Link className={rowClassName} href={href!}>
      {inner}
    </Link>
  ) : (
    <div className={rowClassName}>{inner}</div>
  );
};
